import { Injectable } from '@angular/core';
import { DisplayedProduct } from '../index';
import { Observable, Subject, map } from 'rxjs';
import { CategoryResponse } from '@coworkrz/categories/data-access';

import {
  appendUnit,
  productIsSubscription,
  selectPrices,
  sortPricesByMostRelevant,
} from '@coworkrz/core/helpers/js';
import { CategoryCode, ProductUnit } from '@coworkrz/products/types';
import { group } from 'radash';
import { HttpBaseService } from '@coworkrz/core/http';
import {
  CreateProductDto,
  SharedPriceDto,
  SharedProductDto,
} from '@coworkrz/core/dto';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends HttpBaseService {
  private url = 'products';
  private productUpdates$: Subject<SharedProductDto> =
    new Subject<SharedProductDto>();
  private priceUpdates$: Subject<SharedPriceDto> =
    new Subject<SharedPriceDto>();

  create(product: CreateProductDto): Observable<SharedProductDto> {
    return this.http.post<SharedProductDto>(`${this.url}`, product).pipe(
      map((data: SharedProductDto) => {
        this.productUpdates$.next(data);
        return data;
      })
    );
  }
  getProductUpdates(): Subject<SharedProductDto> {
    return this.productUpdates$;
  }
  getCategoryList(query?: {
    [key: string]: string;
  }): Observable<CategoryResponse[]> {
    return this.http.get<CategoryResponse[]>('categories', {
      params: this.createHttpParams(query),
    });
  }

  getList(query?: { [key: string]: any }): Observable<SharedProductDto[]> {
    return this.http.get<SharedProductDto[]>(this.url, {
      params: this.createHttpParams(query),
    });
  }

  productsHaveSameCategory(products: DisplayedProduct[]) {
    const length = products.length;

    if (!length) return { hasSameCategory: false };

    const productsPerCategory = group(products, (product) =>
      String(product.cartItemCategory)
    );
    const categories = Object.values(CategoryCode);
    const matchedCategory = categories.find(
      (category) => productsPerCategory[category]?.length === length
    );

    if (matchedCategory) {
      return {
        hasSameCategory: true,
        category: matchedCategory,
      };
    }
    return { hasSameCategory: false };
  }

  getOne(id: string): Observable<SharedProductDto> {
    return this.http.get<SharedProductDto>(`${this.url}/${id}`);
  }

  update(id: string, product: SharedProductDto): Observable<SharedProductDto> {
    return this.http.put<SharedProductDto>(`${this.url}/${id}`, product).pipe(
      map((data: SharedProductDto) => {
        this.productUpdates$.next(data);
        return data;
      })
    );
  }

  private getPrices(product: SharedProductDto) {
    const productCopy = JSON.parse(JSON.stringify(product)) as SharedProductDto;
    const sortedPrices = sortPricesByMostRelevant(productCopy.prices);
    const normalPrice = sortedPrices.find(
      (price) => !price.endDate
    ) as SharedPriceDto;
    const discountedPrice =
      sortedPrices.find((price) => price.endDate) ?? normalPrice;
    const currentPrice = discountedPrice ?? normalPrice;

    return {
      normalPrice: {
        ...normalPrice,
        isSubscription: productIsSubscription(normalPrice),
      },
      discountedPrice: {
        ...discountedPrice,
        isSubscription: productIsSubscription(discountedPrice),
      },
      currentPrice: {
        ...currentPrice,
        isSubscription: productIsSubscription(currentPrice),
      },
    };
  }

  private getPricePerSize(products: SharedProductDto[]) {
    return products.map((product) => {
      const parsedProduct = JSON.parse(
        JSON.stringify(product)
      ) as DisplayedProduct;

      const { currentPrice, normalPrice, discountedPrice } =
        this.getPrices(parsedProduct);
      return {
        ...parsedProduct,
        normalPrice,
        discountedPrice,
        currentPrice,
      };
    });
  }

  parseProduct(
    product: SharedProductDto,
    products?: SharedProductDto[],
    cartItemCategory?: CategoryCode
  ): DisplayedProduct {
    const parsedProduct = JSON.parse(
      JSON.stringify(product)
    ) as DisplayedProduct;

    const { currentPrice, normalPrice, discountedPrice } = selectPrices(
      parsedProduct.prices
    );
    parsedProduct.name = appendUnit(parsedProduct.name, currentPrice.unit);

    let discount = 0;
    if (
      discountedPrice &&
      discountedPrice.amount !== 0 &&
      discountedPrice.amount < normalPrice.amount
    ) {
      discount =
        ((normalPrice.amount - discountedPrice.amount) / normalPrice.amount) *
        100;
    }

    return {
      ...parsedProduct,
      normalPrice,
      discountedPrice,
      currentPrice,
      isSubscription: productIsSubscription(currentPrice),
      categoryCode: parsedProduct.category.code as CategoryCode,
      discount,
      pricesBySize: products ? this.getPricePerSize(products) : products,
      cartItemCategory,
    };
  }

  generateProductBasedOnPrice(
    product: SharedProductDto,
    excludePricesWithSize = false,
    cartItemCategory?: CategoryCode
  ): DisplayedProduct[] {
    const pricesPerUnit = group(product.prices, (price) => price.unit);
    const pricesSmall = pricesPerUnit.SMALL ?? [];
    const pricesMedium = pricesPerUnit.MEDIUM ?? [];
    const pricesLarge = pricesPerUnit.LARGE ?? [];

    const products: SharedProductDto[] = [
      { ...product, prices: pricesSmall },
      { ...product, prices: pricesMedium },
      { ...product, prices: pricesLarge },
    ];

    const parsedProducts = Object.keys(pricesPerUnit)
      .filter((unit) => {
        return (
          !excludePricesWithSize ||
          ![ProductUnit.SMALL, ProductUnit.MEDIUM, ProductUnit.LARGE].includes(
            unit as ProductUnit
          )
        );
      })
      .map((unit) =>
        this.parseProduct(
          {
            ...product,
            prices: pricesPerUnit[unit as ProductUnit] ?? [],
          },
          products,
          cartItemCategory
        )
      );

    if (parsedProducts.length) {
      return parsedProducts;
    }

    if (products.length) {
      return [
        {
          ...product,
          pricesBySize: this.getPricePerSize(products),
          cartItemCategory,
        },
      ] as DisplayedProduct[];
    }

    return parsedProducts;
  }

  getAllProductPrices(id: string): Observable<SharedPriceDto[]> {
    return this.http.get<SharedPriceDto[]>(`${this.url}/price/${id}`);
  }

  createPrice(price: SharedPriceDto): Observable<SharedPriceDto> {
    return this.http.post<SharedPriceDto>(`${this.url}/price`, price).pipe(
      map((data: SharedPriceDto) => {
        this.priceUpdates$.next(data);
        return data;
      })
    );
  }

  updatePrice(id: string, price: SharedPriceDto): Observable<SharedPriceDto> {
    return this.http
      .post<SharedPriceDto>(`${this.url}/price/${id}`, price)
      .pipe(
        map((data: SharedPriceDto) => {
          this.priceUpdates$.next(data);
          return data;
        })
      );
  }

  getPriceUpdates(): Subject<SharedPriceDto> {
    return this.priceUpdates$;
  }
}
